'use client';

import React from 'react';
import Link from 'next/link';
import { productAPI } from '@/lib/api';
import type { Product } from '@/types/api';
import { getImageUrl } from '@/lib/utils';

interface HotProductItem {
  id: string;
  name: string;
  slug: string;
  category: string;
  thumb?: string;
}

interface HotProductsProps {
  limit?: number;
}

/**
 * 热门产品组件
 * 显示侧边栏中的热门产品列表
 */
export default function HotProducts({ limit = 4 }: HotProductsProps) {
  const [products, setProducts] = React.useState<HotProductItem[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchHotProducts = async () => {
      try {
        setLoading(true);
        const response = await productAPI.getList({
          limit: limit,
          status: 'published',
          featured: true, // 获取推荐产品
        });

        if (response && response.data) {
          const productItems: HotProductItem[] = response.data.map((product: Product) => ({
            id: product.id,
            name: product.title,
            slug: product.slug,
            category: product.category?.name || 'Product',
            thumb: product.images?.[0]?.imageUrl || undefined,
          }));
          setProducts(productItems);
        }
      } catch (error) {
        console.error('Failed to fetch hot products:', error);
        // 使用默认数据作为后备
        setProducts([
          { id: '1', name: 'SMG200C-6', slug: 'smg200c-6', category: 'Concrete Machinery' },
          { id: '2', name: 'SAC2200', slug: 'sac2200', category: 'Crane Machinery' },
          { id: '3', name: 'SY55C', slug: 'sy55c', category: 'Excavator' },
          { id: '4', name: 'SPR400', slug: 'spr400', category: 'Road Machinery' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchHotProducts();
  }, [limit]);

  if (loading) {
    return (
      <div className="hot-products">
        {[...Array(limit)].map((_, i) => (
          <div key={i} className="hot-product-item" style={{ opacity: 0.5 }}>
            <div className="product-thumb">...</div>
            <div className="product-info">
              <div className="product-name" style={{ color: '#ccc' }}>Loading...</div>
              <span className="product-category">...</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="hot-products">
      {products.map((product) => {
        // 生成产品缩略图文字（取前3个字符）
        const thumbText = product.name.substring(0, 3).toUpperCase();
        
        return (
          <div key={product.id} className="hot-product-item">
            <div className="product-thumb">
              {product.thumb ? (
                <img 
                  src={getImageUrl(product.thumb)} 
                  alt={product.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '6px',
                  }}
                  onError={(e) => {
                    // 如果图片加载失败，显示文字缩略图
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent && !parent.textContent) {
                      parent.textContent = thumbText;
                    }
                  }}
                />
              ) : (
                thumbText
              )}
            </div>
            <div className="product-info">
              <Link href={`/products/${product.slug}`} className="product-name">
                {product.name}
              </Link>
              <span className="product-category">{product.category}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

